import { useState, useEffect } from 'react';
import { Sparkles, X, Check, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { AINoteSuggestion, NotePhase } from '@/types/ai.types';

interface AIOptimizePanelProps {
  suggestion: AINoteSuggestion | null;
  loading: boolean;
  onApply: (edited: AINoteSuggestion) => void;
  onDiscard: () => void;
  onRequestAnalyze: () => void;
}

const PHASE_LABELS: Record<NotePhase, string> = {
  takeoff: '起飞',
  cruise: '巡航',
  landing: '落地',
  crew: '机组',
  general: '通用',
};

export function AIOptimizePanel({
  suggestion,
  loading,
  onApply,
  onDiscard,
  onRequestAnalyze,
}: AIOptimizePanelProps) {
  const [edited, setEdited] = useState<AINoteSuggestion | null>(null);

  // 当外部 suggestion 变化时同步
  useEffect(() => {
    if (suggestion) {
      setEdited(suggestion);
    }
  }, [suggestion]);

  if (!edited) {
    return (
      <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">AI 优化建议</h4>
            <p className="text-xs text-gray-500 mt-1">
              AI 可以根据你写的内容推荐标题、标签和关联维度。
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={onRequestAnalyze}
              disabled={loading}
              className="mt-3 gap-1"
            >
              {loading ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {loading ? '分析中...' : '使用 AI 优化'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const confidenceColor =
    edited.confidence >= 0.8 ? 'text-green-600 bg-green-50' : edited.confidence >= 0.5 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';

  return (
    <div className="border border-blue-200 bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h4 className="text-sm font-medium text-gray-900">AI 优化建议</h4>
        </div>
        <button onClick={onDiscard} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {/* 标题 */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">建议标题</label>
          <Input
            value={edited.suggestedTitle}
            onChange={(e) => setEdited({ ...edited, suggestedTitle: e.target.value })}
            className="text-sm"
          />
        </div>

        {/* 标签 */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">建议标签（逗号分隔）</label>
          <Input
            value={edited.suggestedTags.join(', ')}
            onChange={(e) =>
              setEdited({
                ...edited,
                suggestedTags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
              })
            }
            className="text-sm"
          />
        </div>

        {/* 阶段 */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">建议维度</label>
          <select
            value={edited.suggestedPhase}
            onChange={(e) => setEdited({ ...edited, suggestedPhase: e.target.value as NotePhase })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(PHASE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {/* 关联信息只读展示 */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-gray-50 rounded">
            <span className="text-gray-500">航线：</span>
            <span className="font-medium">{edited.suggestedRouteId ?? '无'}</span>
          </div>
          <div className="p-2 bg-gray-50 rounded">
            <span className="text-gray-500">起飞：</span>
            <span className="font-medium">{edited.suggestedDepartureAirportCode ?? '无'}</span>
          </div>
          <div className="p-2 bg-gray-50 rounded">
            <span className="text-gray-500">着陆：</span>
            <span className="font-medium">{edited.suggestedArrivalAirportCode ?? '无'}</span>
          </div>
          <div className="p-2 bg-gray-50 rounded">
            <span className="text-gray-500">机组：</span>
            <span className="font-medium">{edited.suggestedCrewIds.length > 0 ? edited.suggestedCrewIds.join(', ') : '无'}</span>
          </div>
        </div>

        {/* 置信度和推理 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">AI 置信度：</span>
            <span className={cn('text-xs px-2 py-0.5 rounded font-medium', confidenceColor)}>
              {Math.round(edited.confidence * 100)}%
            </span>
          </div>
          {edited.reasoning && (
            <div className="flex items-start gap-1.5 text-xs text-gray-500">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{edited.reasoning}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={() => onApply(edited)} className="gap-1 flex-1">
          <Check className="w-3.5 h-3.5" />
          采用建议
        </Button>
        <Button size="sm" variant="outline" onClick={onRequestAnalyze} disabled={loading} className="gap-1">
          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          重新分析
        </Button>
      </div>
    </div>
  );
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
